import { useCallback, useMemo } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { Box, Skeleton, useTheme } from '@/ui/primitives';
import { s } from '../../../theme/tokens';
import { DataGrid } from '../../primitives/DataGrid';
import { resolveGridColumnAlignment, wrapDataGridColumns } from './wrapDataGridColumns';
import type { SpacingToken } from '@/theme/tokens';
import type {
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

type LoadingRow = { id: string; __loading: true };

const skeletonWidthSteps = [38, 44, 51, 58, 65, 72, 79] as const;
const CLONE_TABLE_SKELETON_HEIGHT = 12;

const hashSkeletonKey = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const resolveCellSkeletonWidth = (params: { id: GridRowId; field: string }): string => {
  const key = `${String(params.id)}:${params.field}`;
  const width = skeletonWidthSteps[hashSkeletonKey(key) % skeletonWidthSteps.length];
  return `${width}%`;
};

const isCloneSparklineCell = (cellClassName: string | undefined): boolean =>
  typeof cellClassName === 'string' &&
  [
    'tests-table-clone-passrate-cell',
    'tests-table-clone-frequency-cell',
    'tests-table-clone-avgduration-cell',
  ].includes(cellClassName);

const isCloneStructuredCell = (field: string, cellClassName: string | undefined): boolean =>
  cellClassName === 'tests-table-clone-test-cell' ||
  field === 'latestExecutionStatus' ||
  field === 'latestBuildStatus' ||
  isCloneSparklineCell(cellClassName) ||
  field === 'actions';

const renderCloneCellSkeleton = (
  params: { id: GridRowId; field: string },
  options: {
    field: string;
    cellClassName: string | undefined;
    cellJustifyContent: 'flex-start' | 'center' | 'flex-end';
  },
): JSX.Element => {
  if (options.cellClassName === 'tests-table-clone-test-cell') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', pt: '4px' }}>
        <Skeleton
          variant="text"
          width={resolveCellSkeletonWidth(params)}
          sx={{ height: CLONE_TABLE_SKELETON_HEIGHT, transform: 'none' }}
        />
        <Skeleton
          variant="text"
          width="72%"
          sx={{ height: CLONE_TABLE_SKELETON_HEIGHT, transform: 'none' }}
        />
        <Skeleton
          variant="rounded"
          width="84px"
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none', borderRadius: '999px' }}
        />
      </Box>
    );
  }

  if (options.field === 'latestExecutionStatus' || options.field === 'latestBuildStatus') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', pt: '8px' }}>
        <Skeleton
          variant="rounded"
          width="112px"
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none', borderRadius: '999px' }}
        />
        <Skeleton
          variant="text"
          width="72px"
          sx={{ height: CLONE_TABLE_SKELETON_HEIGHT, transform: 'none' }}
        />
        <Skeleton
          variant="text"
          width="96px"
          sx={{ height: CLONE_TABLE_SKELETON_HEIGHT, transform: 'none' }}
        />
      </Box>
    );
  }

  if (isCloneSparklineCell(options.cellClassName)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          minHeight: 0,
          pt: '8px',
        }}
      >
        <Skeleton
          variant="text"
          width="96px"
          sx={{ height: CLONE_TABLE_SKELETON_HEIGHT, transform: 'none' }}
        />
        <Skeleton
          variant="rounded"
          width="92%"
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{
            transform: 'none',
            borderRadius: '10px',
            alignSelf: options.cellJustifyContent,
            marginBottom: '2px',
          }}
        />
      </Box>
    );
  }

  if (options.field === 'actions') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', pt: '8px' }}>
        <Skeleton
          variant="circular"
          width={CLONE_TABLE_SKELETON_HEIGHT}
          height={CLONE_TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none' }}
        />
      </Box>
    );
  }

  return (
    <Skeleton
      variant="text"
      width={resolveCellSkeletonWidth(params)}
      sx={{
        height: componentSizes.dataGrid.skeletonHeight,
        transform: 'none',
        maxWidth: '100%',
      }}
    />
  );
};

type SkeletonDataGridProps<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  loadingRowCount?: number;
  totalRowCount?: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  paginationModel?: GridPaginationModel;
  autoHeight?: boolean;
  showToolbar?: boolean;
  getRowId: (row: T) => GridRowId;
  getRowHeight?: DataGridProps['getRowHeight'];
  cellPaddingY?: SpacingToken;
  columnVisibilityModel?: DataGridProps['columnVisibilityModel'];
  slotProps?: DataGridProps['slotProps'];
  localeText?: DataGridProps['localeText'];
  sx?: DataGridProps['sx'];
  hideFooter?: boolean;
};

const resolvePadding = (token: SpacingToken): string | number => {
  switch (token) {
    case 'none':
      return 0;
    case 'xxxs':
      return s('xxs');
    case 'xxs':
    case 'xs':
      return s('xs');
    case 'sm':
      return s('sm');
    case 'md':
      return s('md');
    case 'lg':
      return s('lg');
    case 'xl':
      return s('xl');
    case '2xl':
      return s('2xl');
    case '3xl':
      return s('3xl');
    case '4xl':
      return s('4xl');
    case '5xl':
      return s('5xl');
    case 'gutter':
      return s('gutter');
    case 'section':
      return s('section');
    default:
      return s('3xl');
  }
};

export const SkeletonDataGrid = <T extends GridValidRowModel>({
  columns,
  loadingRowCount = 10,
  totalRowCount,
  pageSizeOptions = [5, 10, 25, 50],
  pageSize,
  paginationModel,
  autoHeight = false,
  showToolbar = false,
  getRowId,
  getRowHeight,
  cellPaddingY,
  columnVisibilityModel,
  slotProps,
  localeText,
  sx,
  hideFooter = false,
}: SkeletonDataGridProps<T>): ReactElement => {
  const theme = useTheme();
  const resolvedPageSize =
    pageSize && pageSizeOptions.includes(pageSize) ? pageSize : (pageSizeOptions[0] ?? 5);

  const loadingRows = useMemo<LoadingRow[]>(
    () =>
      Array.from({ length: loadingRowCount }, (_value, index) => ({
        id: `loading-${index}`,
        __loading: true,
      })),
    [loadingRowCount],
  );

  const columnsWithLoading = useMemo(() => {
    const skeletonColumns = columns.map((column) => {
      const { align, headerAlign } = resolveGridColumnAlignment(column);
      const resolvedCellClassName =
        typeof column.cellClassName === 'string' ? column.cellClassName : undefined;
      const isStructuredCloneCell = isCloneStructuredCell(column.field, resolvedCellClassName);
      const cellJustifyContent =
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      const headerJustifyContent =
        headerAlign === 'center' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start';
      return {
        ...column,
        renderHeader: (params) => {
          const computedWidth = params.colDef.computedWidth;
          const widthSource =
            typeof computedWidth === 'number'
              ? computedWidth
              : typeof column.width === 'number'
                ? column.width
                : typeof column.minWidth === 'number'
                  ? column.minWidth
                  : null;
          const headerSkeletonWidth =
            typeof widthSource === 'number'
              ? Math.max(
                  componentSizes.dataGrid.columns.xs,
                  Math.min(widthSource * 0.6, componentSizes.dataGrid.columns.max),
                )
              : '60%';
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: headerJustifyContent,
                height: '100%',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="text"
                width={headerSkeletonWidth}
                sx={{
                  height: componentSizes.dataGrid.skeletonHeight,
                  transform: 'none',
                  maxWidth: '100%',
                }}
              />
            </Box>
          );
        },
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: isStructuredCloneCell ? 'flex-start' : 'center',
              justifyContent:
                column.field === 'actions'
                  ? 'flex-end'
                  : isStructuredCloneCell
                    ? 'flex-start'
                    : cellJustifyContent,
              height: '100%',
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: isStructuredCloneCell ? 'visible' : 'hidden',
            }}
          >
            {renderCloneCellSkeleton(params, {
              field: column.field,
              cellClassName: resolvedCellClassName,
              cellJustifyContent,
            })}
          </Box>
        ),
      };
    }) as Array<GridColDef<T>>;
    return wrapDataGridColumns(skeletonColumns, {
      getBaseCellSx: () => (cellPaddingY ? { py: resolvePadding(cellPaddingY) } : undefined),
    });
  }, [cellPaddingY, columns]);

  const resolveRowId = useCallback(
    (row: T) => {
      if ('__loading' in row) {
        return (row as unknown as LoadingRow).id;
      }
      return getRowId(row);
    },
    [getRowId],
  );

  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    '& .MuiDataGrid-footerContainer': {
      borderTop: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
    },
  };
  const resolvedSx: SxProps<Theme> = [
    baseSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as SxProps<Theme>;
  const resolvedPaginationModel = paginationModel ?? { page: 0, pageSize: resolvedPageSize };
  const resolvedRowCount = typeof totalRowCount === 'number' ? totalRowCount : loadingRows.length;
  const showFooterSkeleton = !hideFooter && typeof totalRowCount !== 'number';
  const defaultSlotProps: NonNullable<DataGridProps['slotProps']> | null =
    typeof totalRowCount === 'number'
      ? null
      : {
          pagination: {
            labelDisplayedRows: () => '',
          },
        };
  const resolvedSlotProps: DataGridProps['slotProps'] = defaultSlotProps
    ? slotProps
      ? {
          ...defaultSlotProps,
          ...slotProps,
          pagination: {
            ...defaultSlotProps.pagination,
            ...(slotProps.pagination ?? {}),
          },
        }
      : defaultSlotProps
    : slotProps;

  return (
    <>
      <DataGrid
        rows={loadingRows as unknown as T[]}
        columns={columnsWithLoading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterSelectedRowCount
        hideFooter={hideFooter || showFooterSkeleton}
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        paginationModel={resolvedPaginationModel}
        rowCount={resolvedRowCount}
        getRowId={resolveRowId}
        initialState={{ pagination: { paginationModel: resolvedPaginationModel } }}
        autoHeight={autoHeight}
        getRowHeight={getRowHeight}
        showToolbar={showToolbar}
        columnVisibilityModel={columnVisibilityModel}
        slotProps={resolvedSlotProps}
        localeText={localeText}
        sx={resolvedSx}
      />
      {showFooterSkeleton ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            minHeight: theme.app.density.dataGrid.headerHeight,
            height: theme.app.density.dataGrid.headerHeight,
            paddingLeft: s('xxs'),
            paddingRight: s('none'),
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Skeleton
            variant="text"
            width={componentSizes.dataGrid.columns.md}
            sx={{ height: componentSizes.dataGrid.skeletonHeight, transform: 'none' }}
          />
        </Box>
      ) : null}
    </>
  );
};
